import React from 'react'
import { Div, Flex, Text } from '@sendoutcards/quantum-design-ui'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { useSelector, useState } from 'src/hooks'

type ReicipientType = {
  firstname: string
  lastname: string
  imageUrl?: string
}

type ActionType = {
  title?: string
  onClick: () => void
}

export type RecipientViewProps = {
  title?: string
  sender: ReicipientType
  primaryAction?: ActionType
  secondaryAction?: ActionType
}

export const RecipientView = (props: RecipientViewProps) => {
  const { title, sender, primaryAction, secondaryAction } = props
  const fontFamily = 'Montserrat, sans-serif'

  const windowWidth = useSelector(state => state.window.width)
  const deviceBreakpoints = {
    mobile: 906,
    tablet: 710,
    phone: 512,
  }

  const isPhone = windowWidth <= deviceBreakpoints.phone
  const isTablet = windowWidth <= deviceBreakpoints.tablet
  const isMobileDevice = windowWidth <= deviceBreakpoints.mobile

  const senderStyles = sender.imageUrl
    ? {
        aspectRatio: '1/1',
        width: '44px',
        height: '44px',
        minWidth: '44px',
        backgroundImage: `url(${sender.imageUrl})`,
        backgroundPostition: 'center',
        backgroundSize: 'contain',
        backgrounRepeat: 'no-repeat',
        borderRadius: '50px',
      }
    : {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50px',
        color: '#fff',
        backgroundColor: '#7C7C7C',
        boxShadow: '0px 0px 16px 0px rgba(0, 0, 0, 0.06)',
        fontSize: '20px',

        width: '44px',
        minWidth: '44px',
        height: '44px',
      }

  const actionSharedStyles = {
    display: 'flex',
    cursor: 'pointer',
    borderRadius: '16px',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px 12px',
    flexDirection: 'row' as const,
    columnGap: '6px',
  }

  const recipientInitals = `${sender.firstname[0] + sender.lastname[0]}`

  const [isExpanded, setIsExpanded] = useState(false)
  const maxLength = (() => {
    if (isPhone) return 25
    if (isTablet) return 45
    if (isMobileDevice) return 70
    return 101
  })()

  const toggleExpanded = () => setIsExpanded(prev => !prev)

  const shouldTruncate = isMobileDevice && title && title.length > maxLength
  const displayText =
    shouldTruncate && !isExpanded ? `${title.slice(0, maxLength)}...` : title

  return (
    <Flex flexDirection="column" rowGap="x2" width="100%">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex flexDirection="column" alignItems="flex-start">
          <Text
            type="body"
            style={{
              color: '#404040',
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: fontFamily,
            }}
          >
            {displayText}
            {shouldTruncate && (
              <span
                onClick={toggleExpanded}
                style={{
                  color: '#727272c4',
                  fontSize: '10px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginTop: '4px',
                  fontFamily: fontFamily,
                  whiteSpace: 'nowrap',
                }}
              >
                {isExpanded ? '(Show Less)' : 'View More'}
              </span>
            )}
          </Text>
          <Text
            type="body"
            content="FROM"
            style={{
              color: '#B7B7B7',
              fontSize: '8px',
              fontWeight: 700,
              fontFamily: fontFamily,
              lineHeight: 1.2,
            }}
          />
          <Text
            type="body"
            content={`${sender.firstname} ${sender.lastname}`}
            style={{
              color: '#6B7280',
              fontSize: '12px',
              fontWeight: 500,
              fontFamily: fontFamily,
              lineHeight: 1.2,
            }}
          />
        </Flex>
        <Div style={{ ...senderStyles }}>
          {!sender.imageUrl && recipientInitals}
        </Div>
      </Flex>
      <Flex
        flexDirection="column"
        columnGap="x1"
        width="100%"
        alignItems="center"
      >
        <Div
          onClick={primaryAction?.onClick}
          style={{
            ...actionSharedStyles,
            color: '#fff',
            boxShadow: '0px 0px 16px 0px rgba(0, 0, 0, 0.06)',
            backgroundColor: '#444',
            width: '100%',
            fontSize: '16px',
          }}
        >
          <Icon size={18} name="stars" />
          {primaryAction?.title}
        </Div>
        <Flex
          onClick={secondaryAction?.onClick}
          cursor="pointer"
          justifyContent="center"
          alignItems="center"
          inset={{ top: '10px', bottom: '8px' }}
        >
          <Icon name="print_request" size={16} color="#A168E9" />
          <Text
            type="caption"
            content="Request a Printed Copy"
            outset={{ left: 'x1' }}
            style={{ color: '#A168E9', textDecoration: 'underline' }}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
